/**
 * Хаб «Планирование сезона» — v2 (AK-434, revert #690 + story #6 founder'а
 * 05.08.2026). Единственный экран раздела: `/planning`.
 *
 * Что здесь и почему v2 отличается от v1 (#690):
 *
 *   1. Три секции карточек: «Плановый / Идущий / Завершённые». Классификация —
 *      по ВЫЧИСЛЯЕМОМУ статусу сезона (`groupSeasonsByPhase` → `seasonCardStatus`,
 *      канон терминологии v3 §5: даты рамки прошли → «Завершён»; сегодня внутри
 *      рамки → «Идёт»; иначе решает утверждённость бюджета; без дат рамки —
 *      фолбэк на арифметику по году). Хранимая `seasons.status` мертва
 *      (ADR-060 §S8) и не читается. Раньше (v1) классификация шла по
 *      `is_current`/`status` из БД — у всех сезонов было `status='planning'`,
 *      всё падало в «Плановый», секции «Завершённые/Идущий» стояли пустыми.
 *
 *   2. CTA на карточке сезона ведёт в хаб «Бюджет сезона {год}»
 *      (`/planning/seasons/$year/budget`, макет v2-00-hub) — экран входа в
 *      разделы бюджета. Историческая эволюция: v1 вёл на настройки компании
 *      (юзер думал, что настраивает сезон 2004, а менял глобальный шаблон),
 *      v2 открывал Dialog со списком разделов (AK-633), design-pass 20.08
 *      заменил его полноценным экраном по решению founder («по прототипам
 *      там был крутой экран», а не попап).
 *
 *   3. Список сезонов и форма settings удалены как роуты (см. router.tsx).
 *      Список избыточен — хаб уже показывает все видимые сезоны в трёх
 *      секциях; форма settings была дублем `/company/season`.
 *
 * Гейт раздела (SA-03 §2) остаётся: пока `season_config` не сохранена —
 * экран закрыт, ведёт в `/company/season`.
 *
 * Мультикомпанийность (AK-541 P1-2, восстановлено в AK-544). `/planning` ходит
 * в бэк через `resolve_company_id`, который в тенанте с НЕСКОЛЬКИМИ компаниями
 * отдаёт 409 `company_ambiguous`, а в пустом — 404. Обе ветки имеют свои экраны
 * (`PickCompanyState` / `NoCompanyState`), иначе юзер получает тупик «Обновите
 * страницу» и холостую ссылку из «Настройки → Сезон» (`?company_id=`). При
 * переписывании хаба на v2 (#690 → revert #692 → #693) эта обработка выпала —
 * revert вернул её в `SeasonSettingsTab`, но не в хаб. Регрессия латентная: при
 * одной компании ветки недостижимы, поэтому её не ловит ни tsc, ни e2e.
 *
 * Термины — канон v3 (`docs/superpowers/specs/2026-08-07-season-terminology-v3.md`,
 * утв. founder 07.08): контейнер = «Бюджет сезона {год}», набор справочников =
 * «Бюджетные справочники», «производственная программа» = имя УТВЕРЖДЁННОГО
 * бюджета в отчётной речи (не имя подраздела). Правки #697 по этому файлу
 * вобраны сюда — там файл переписывается целиком, отдельным PR они бы повисли.
 *
 * Долг с вердикта head-web на sha 50223e51 («фаза по календарному году
 * игнорирует `boundary_type='agro_year'`») закрыт этим ребейзом: классификация
 * теперь идёт по датам рамки сезона (`seasonCardStatus`), которые несут
 * фактическую границу независимо от `boundary_type`; арифметика по году —
 * только фолбэк, когда рамка не пришла вовсе.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { BarChart3, CalendarCheck2, Lock, PieChart, Settings2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  PLAN_STATUS_TITLES,
  SEASON_PHASE_TITLES,
  classifySeasonConfigError,
  getSeasonConfig,
  groupSeasonsByPhase,
  listSeasonCards,
  seasonCardStatus,
  type SeasonCard,
  type SeasonPhase,
} from '@/lib/planning/api';
import {
  SEASON_STATUS_BADGE,
  SEASON_STATUS_TITLES,
} from '@/lib/season/computedStatus';
import { formatRuDate } from '@/lib/dateFmt';
import { NoCompanyState, PickCompanyState } from './EmptyCompanyStates';

/**
 * Кнопка карточки — переход в хаб «Бюджет сезона» (макет v2-00-hub, решение
 * founder 20.08: раньше тут открывался Dialog со списком разделов — «по
 * прототипам там был крутой экран», теперь он есть).
 *
 * Иерархия по фазе (design-pass 20.08): primary только у планового сезона
 * (там сейчас идёт работа), идущий — outline, завершённый — ghost. Раньше
 * плановый и идущий оба были primary — на экране спорили две главные кнопки.
 */
function SeasonCardCta({ phase, year }: { phase: SeasonPhase; year: number }) {
  const variant = phase === 'past' ? 'ghost' : phase === 'future' ? 'default' : 'outline';
  return (
    <Button asChild size="sm" variant={variant}>
      <Link to="/planning/seasons/$year/budget" params={{ year: String(year) }}>
        {phase !== 'past' && <Settings2 className="mr-2 h-4 w-4" />}
        {phase === 'future' ? 'Настроить бюджет сезона' : 'Открыть бюджет сезона'}
      </Link>
    </Button>
  );
}

/** «0 из 3 утвердили» — короткое значение для ячейки, подпись даёт `dt`. */
function farmsMetaValue(card: SeasonCard): string {
  if (card.farms_total <= 0) return 'нет в доступе';
  if (card.farms_total === 1) {
    return card.farms_planned >= 1 ? 'программа утверждена' : 'программа не утверждена';
  }
  return `${card.farms_planned} из ${card.farms_total} утвердили`;
}

/** Ячейка «подпись — значение» в карточке сезона (v2-00: labeled-колонки, не текстовая строка). */
function SeasonMetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm leading-6 text-foreground">{value}</dd>
    </div>
  );
}

function SeasonHubCard({ card, phase }: { card: SeasonCard; phase: SeasonPhase }) {
  const isPast = phase === 'past';
  // Статус сезона — вычисляемый (канон v3 §5); статус бюджета идёт отдельной
  // ячейкой ниже, чтобы прошедший сезон не подписывался «планируется».
  const seasonStatus = seasonCardStatus(card);
  return (
    <Card className={isPast ? 'opacity-80' : undefined}>
      <CardHeader className="p-4 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold text-foreground">
            {card.name?.trim() || `Бюджет сезона ${card.year}`}
          </span>
          <Badge variant={SEASON_STATUS_BADGE[seasonStatus]}>
            {SEASON_STATUS_TITLES[seasonStatus]}
          </Badge>
          <span className="ml-auto text-xs text-muted-foreground">
            {/* Даты — по-русски, как «дедлайн защиты» ниже. ISO-строка из API
                на карточке читалась как машинный лог (design review 18.08 А2). */}
            {card.starts_on && card.ends_on
              ? `${formatRuDate(card.starts_on)} — ${formatRuDate(card.ends_on)}`
              : `год ${card.year}`}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {/* Метрики карточки — labeled-ячейки, как статус-полоса макета
            v2-00-hub («Статус сезона / Почему / Статус бюджета»). Раньше это
            была одна текстовая строка из muted-span'ов — читалась простынёй
            (претензия founder, design-pass 20.08). */}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
          <SeasonMetaCell
            label="Бюджет сезона"
            value={card.status_title || PLAN_STATUS_TITLES[card.status]}
          />
          <SeasonMetaCell label="Хозяйства" value={farmsMetaValue(card)} />
          <SeasonMetaCell
            label="Бюджетные справочники"
            value={`${card.catalogs_approved} из ${card.catalogs_total} утверждены`}
          />
          <SeasonMetaCell
            label="Сценарии"
            value={card.scenarios_count > 0 ? String(card.scenarios_count) : 'пока нет'}
          />
          {card.plan_due_on && (
            <SeasonMetaCell label="Дедлайн защиты" value={formatRuDate(card.plan_due_on)} />
          )}
        </dl>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SeasonCardCta phase={phase} year={card.year} />
        </div>
      </CardContent>
    </Card>
  );
}

function PhaseSection({ phase, cards }: { phase: SeasonPhase; cards: SeasonCard[] }) {
  return (
    <section aria-labelledby={`phase-${phase}`}>
      <div className="mb-2 flex items-center gap-2">
        <h2 id={`phase-${phase}`} className="text-sm font-semibold text-foreground">
          {SEASON_PHASE_TITLES[phase]}
        </h2>
        <span className="text-xs text-muted-foreground">
          {cards.length === 0
            ? 'пока пусто'
            : `${cards.length} ${cards.length === 1 ? 'сезон' : 'сезонов'}`}
        </span>
      </div>
      {cards.length === 0 ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            {phase === 'past' &&
              'Завершённых сезонов пока нет — историю можно подгрузить импортом из AgroHistory.'}
            {phase === 'current' &&
              'В текущем году нет сезона в окне видимости. Проверьте настройку сезона в «Настройки компании · Сезон».'}
            {phase === 'future' &&
              'Планового сезона нет. Расширьте окно «будущих сезонов» в настройках компании.'}
          </CardContent>
        </Card>
      ) : (
        <Stack gap={3}>
          {cards.map((c) => (
            // Ключ — год: `season_id` у незаведённого сезона `null` (ADR-060
            // §S6), и на паре плановых лет React получал два одинаковых ключа.
            <SeasonHubCard key={c.year} card={c} phase={phase} />
          ))}
        </Stack>
      )}
    </section>
  );
}

/** Закрытый раздел — настройка сезона ещё не сохранена (гейт SA-03 §2). */
function GateClosed() {
  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <span className="text-base font-semibold text-foreground">
            Раздел закрыт — сначала настройте сезон
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          <p className="text-sm text-muted-foreground">
            Задайте в настройках компании, как называются сезоны, какими датами они
            ограничены и сколько сезонов показывать в списках. После кнопки
            «Сохранить настройки» раздел откроется для всех хозяйств.
          </p>
          <div>
            <Button asChild>
              <Link to="/company/$tab" params={{ tab: 'season' }}>
                <Settings2 className="mr-2 h-4 w-4" />
                Настройки компании · Сезон
              </Link>
            </Button>
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PlanningHubPage() {
  // Как во вкладке настроек: `companyId` ненулевой только после выбора в
  // селекторе (ветка 409); иначе бэк сам находит единственную компанию.
  //
  // Стартовое значение берём из URL `?company_id=` (AK-541 P1-2): вкладка
  // «Настройки → Сезон» пробрасывает выбранную компанию в ссылку на хаб
  // (`SeasonSettingsTab.tsx`, `to="/planning" search={{ company_id }}`), чтобы
  // юзеру в холдинге с несколькими юрлицами не пришлось выбирать её второй раз.
  // Параметр объявлен в `validateSearch` роута `/planning` — если хаб перестанет
  // его читать, ссылка станет холостой. Дальнейший выбор в селекторе живёт в
  // локальном стейте — URL не обновляем, чтобы не путать «где я в холдинге»
  // вне раздела планирования.
  const search = useSearch({ strict: false }) as { company_id?: string };
  const [companyId, setCompanyId] = useState<string | null>(search.company_id ?? null);

  const config = useQuery({
    queryKey: ['planning', 'season-config', companyId],
    // Обёртка, а не bare-ref: getSeasonConfig принимает опциональный
    // company_id, а tanstack прокидывает в queryFn свой QueryFunctionContext —
    // без обёртки arg[0] уехал бы в URL как `?company_id=[object Object]`.
    queryFn: () => getSeasonConfig(companyId),
    staleTime: 60_000,
    // Штатные 4xx (нет компаний / нужен выбор компании) — это не сбой сети, а
    // ответ по контракту. Без этого react-query трижды ретраит 409, и юзер
    // ждёт три круга до селектора компании.
    retry: (failureCount, err) => {
      const kind = classifySeasonConfigError(err);
      if (kind !== 'other') return false;
      return failureCount < 1;
    },
  });
  const cards = useQuery({
    // companyId включён в ключ, чтобы кэши разных компаний не смешивались и
    // ретаргет после смены компании (409-ветка) шёл честно.
    queryKey: ['planning', 'season-cards', 'windowed', companyId],
    // Окно из настройки: past_seasons_visible + future_seasons_visible.
    // Раньше передавали showAll=true — тянули 51 сезон (2000-2050) на клиент.
    // `companyId` симметричен конфигу: `GET /planning/seasons` идёт через тот же
    // `resolve_company_id` и без него отдал бы 409 уже после выбора компании.
    queryFn: () => listSeasonCards(false, companyId),
    enabled: config.data?.is_configured === true,
    staleTime: 30_000,
  });

  const configured = config.data?.is_configured === true;
  const groups = cards.data ? groupSeasonsByPhase(cards.data) : null;

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1200px] pb-safe-fab">
        <PageHeader
          title="Планирование сезона"
          showBack={false}
          description="Три секции сезонов: завершённые (история), текущий и следующий плановый. Открытие сезона ведёт в сборку его бюджета."
        />

        <Stack gap={4} className="mt-4">
          {config.isLoading && <Skeleton className="h-24 w-full" />}

          {/*
            Три штатных ветки бэка (AK-541 / AK-544): 404 «нет компаний» и 409
            «в холдинге несколько» имеют собственные экраны с осмысленной
            навигацией. Иначе — общий текст «Обновите страницу». `/planning`
            ходит через `resolve_company_id` ровно так же, как вкладка настроек,
            и обязан покрывать эти два случая, а не показывать тупик.
          */}
          {config.isError && (() => {
            const kind = classifySeasonConfigError(config.error);
            if (kind === 'no_company') {
              return <NoCompanyState hint="сможете открыть планирование сезона" />;
            }
            if (kind === 'company_ambiguous') {
              return (
                <PickCompanyState
                  onPick={(id) => setCompanyId(id)}
                  title="Для какой компании смотрим планирование?"
                  hint="Бюджет сезона у каждой компании свой — выберите, для какой сейчас работаем."
                />
              );
            }
            return (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-destructive">
                    Не удалось загрузить настройку сезона. Обновите страницу.
                  </p>
                </CardContent>
              </Card>
            );
          })()}
          {config.data && !configured && <GateClosed />}

          {config.data && configured && (
            <>
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
                <CalendarCheck2 className="h-4 w-4 text-primary" />
                <span className="text-foreground">Сезоны настроены — раздел доступен</span>
                <span className="text-muted-foreground">
                  окно: {config.data.visible_past_years} прошлых и{' '}
                  {config.data.visible_future_years} будущих
                </span>
                <Button asChild variant="ghost" size="sm" className="ml-auto">
                  <Link to="/company/$tab" params={{ tab: 'season' }}>
                    Настройки компании · Сезон
                  </Link>
                </Button>
              </div>

              {/* AK-740 — «Обзор СПП»: этап анализа ПЕРЕД планированием сезона
                  (JTBD founder 23.08, «я приступаю к планированию 2027, мне
                  надо понять, что было в прошлом году»). Drill-down компания
                  → кластер → хозяйство, годы факта + варианты будущего. */}
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
                <PieChart className="h-4 w-4 text-primary" />
                <span className="text-foreground">Обзор СПП — прошлое, текущее, варианты будущего</span>
                <span className="text-muted-foreground">
                  доли культур по годам — от компании до конкретного хозяйства
                </span>
                <Button asChild variant="ghost" size="sm" className="ml-auto">
                  <Link to="/planning/spp-overview">Открыть обзор СПП →</Link>
                </Button>
              </div>

              {/* Вход 2 в аналитику прошлых лет (решение А founder'а 07.08).
                  Тот же компонент, что в разделе «Аналитика», но с
                  `?from=planning`: крошки бюджета сезона и кнопка «Взять в
                  план». Планировать «из головы», когда история под рукой, —
                  ровно то, чего этот вход не даёт сделать. */}
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span className="text-foreground">Прежде чем ставить план — посмотрите, что было</span>
                <span className="text-muted-foreground">
                  урожайность по культурам и полям за прошлые годы
                </span>
                <Button asChild variant="ghost" size="sm" className="ml-auto">
                  <Link
                    to="/analytics/history"
                    search={{ from: 'planning', company_id: companyId ?? undefined }}
                  >
                    Аналитика прошлых лет →
                  </Link>
                </Button>
              </div>

              {cards.isLoading && (
                <Stack gap={4}>
                  <Skeleton className="h-28 w-full" />
                  <Skeleton className="h-28 w-full" />
                  <Skeleton className="h-28 w-full" />
                </Stack>
              )}
              {cards.isError && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-destructive">
                      Не удалось загрузить сезоны. Обновите страницу.
                    </p>
                  </CardContent>
                </Card>
              )}

              {groups && (
                <>
                  <PhaseSection phase="future" cards={groups.future} />
                  <PhaseSection phase="current" cards={groups.current} />
                  <PhaseSection phase="past" cards={groups.past} />
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}
